import { vec3 } from 'gl-matrix';
import { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import type { RoiId } from '~/domain/changes/ids';
import type { PlaneMm } from '~/domain/geometry/patientGeometry';
import type { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import type { Environment } from '~/engine/data/Environment';
import type { DatasetEnvironmentRegistry } from '~/engine/scene/DatasetEnvironmentRegistry';
import { viewShowsAcquiredPrimarySlice } from '~/engine/view/planeViewGeometry';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { useAcquiredPrimarySliceShown } from '../environment/useEnvironmentViews';
import { useSubscriptionVersion } from '../environment/useSubscriptionVersion';
import type { SeriesInstanceUid } from '~/domain/dicom/uids';

export interface ActiveEditTarget {
    /** Live Environment holding the target's structure set. */
    environment: Environment | undefined;
    /** The active structure set — the sidebar selection. */
    structureSet: StructureSetReadModel | undefined;
    /** The selected structure; null when none. */
    roiId: RoiId | null;
}

/**
 * Environments are minted and disposed outside Recoil (registry
 * release/re-acquire), so resolving them at render time alone would go stale.
 * A lifecycle notification re-renders the subscriber, re-running the
 * resolution against the live registry.
 */
export function useRegistryLifecycleVersion(): void {
    useSubscriptionVersion(subscribeRegistryLifecycle);
}

function subscribeRegistryLifecycle(listener: () => void): () => void {
    return getDatasetEnvironmentRegistry().subscribeLifecycle(listener);
}

/**
 * Resolves the single edit target every ROI-editing toolbar control acts on:
 * the active structure set — the sidebar selection — in the registry
 * Environment of its dataset, and the global selected ROI. An unresolved
 * Environment yields `environment: undefined`, and consumers disable editing.
 */
export function useActiveEditTarget(): ActiveEditTarget {
    const structureSet = useRecoilValue(currentStructureSetState);
    const roiId = useRecoilValue(selectedROIState);
    useRegistryLifecycleVersion();

    return {
        environment: resolveActiveEnvironment(
            getDatasetEnvironmentRegistry(),
            structureSet
        ),
        structureSet,
        roiId,
    };
}

export interface ActiveSlicePlane {
    /**
     * The edit target's viewed slice plane; `null` when unresolved.
     * Subscribed: tracks scrolling.
     */
    plane: PlaneMm | null;
    /**
     * Fresh read at call time. Apply handlers must use this, never a value
     * captured at render — a scroll between render and click would otherwise
     * act on the previously shown slice.
     */
    readPlane: () => PlaneMm | null;
}

/**
 * The edit target's acquired slice plane: the plane through the active
 * Environment's plane-intersection point along its image grid's slice
 * normal. Not any one tile's plane: a sagittal or coronal tile scrolls
 * along its own normal, while the slice-scoped operations are defined on
 * the image's slice plane. Re-renders the caller on every scroll of that
 * point.
 *
 * Offered only while a tile of the edit target's Environment shows an
 * acquired slice of its primary image ({@link useAcquiredPrimarySliceShown}).
 * `null` while unresolved, while no tile of the target is on the grid,
 * while every tile of it is turned off the acquired slices, while each tile
 * in the acquired slice plane draws the fused secondary image alone, and on
 * a tilted acquisition, which no patient-aligned tile cuts along its
 * slices, so an operation never changes a slice no view shows. Which tile
 * the pointer last crossed plays no part. `readPlane` checks the tiles
 * again at the call.
 */
export function useShownSlicePlane(): ActiveSlicePlane {
    const structureSet = useRecoilValue(currentStructureSetState);
    const viewManager = useRecoilValue(viewManagerState);
    useRegistryLifecycleVersion();

    // The Environment's plane-intersection point is the slice truth; its
    // observable notifies per scroll tick. The Environment identity is
    // stable while resident, so this resubscribes only across re-mints.
    const environment = resolveActiveEnvironment(
        getDatasetEnvironmentRegistry(),
        structureSet
    );
    const subscribeScroll = useMemo(
        () =>
            environment
                ? (listener: () => void) => {
                      environment.planeIntersectionPoint.subscribe(listener);
                      return () =>
                          environment.planeIntersectionPoint.unsubscribe(
                              listener
                          );
                  }
                : undefined,
        [environment]
    );
    useSubscriptionVersion(subscribeScroll);
    const shown = useAcquiredPrimarySliceShown(environment);

    const readPlane = useCallback((): PlaneMm | null => {
        const liveEnvironment = resolveActiveEnvironment(
            getDatasetEnvironmentRegistry(),
            structureSet
        );
        const shownNow =
            !!liveEnvironment &&
            !!viewManager &&
            viewManager
                .viewsFor(liveEnvironment)
                .current.some(viewShowsAcquiredPrimarySlice);
        return shownNow ? acquiredSlicePlaneOf(liveEnvironment) : null;
    }, [structureSet, viewManager]);

    return {
        plane: shown && environment ? acquiredSlicePlaneOf(environment) : null,
        readPlane,
    };
}

/**
 * The plane through `environment`'s plane-intersection point along its
 * image grid's slice normal, or `null` while it has no image.
 */
function acquiredSlicePlaneOf(environment: Environment): PlaneMm | null {
    const grid = environment.image?.parent.grid;
    if (!grid) {
        return null;
    }
    return {
        point: vec3.clone(environment.planeIntersectionPoint.coordinates),
        normal: grid.getUnitZ(),
    };
}

/**
 * The series UID of the active structure set's owning dataset, the editable
 * set. Only tiles of this series may be committed as the drawing
 * view. Undefined when no structure set is active or its dataset is not
 * registered.
 */
export function useActiveEditTargetSeriesUid(): SeriesInstanceUid | undefined {
    const structureSet = useRecoilValue(currentStructureSetState);
    useRegistryLifecycleVersion();
    return resolveEditableEntrySeriesUid(
        getDatasetEnvironmentRegistry(),
        structureSet
    );
}

/**
 * Maps the active structure set to the live Environment of its owning dataset:
 * the owning series resolves to its resident Environment. Returns `undefined`
 * when the dataset is not registered or not currently resident. Pure registry
 * read — arming re-resolves through this per arm, because a captured
 * Environment goes stale across release/re-acquire.
 */
export function resolveActiveEnvironment(
    registry: DatasetEnvironmentRegistry,
    structureSet: StructureSetReadModel | undefined
): Environment | undefined {
    const seriesUid = resolveEditableEntrySeriesUid(registry, structureSet);
    if (seriesUid === undefined) {
        return undefined;
    }
    return registry.environmentFor(seriesUid);
}

/**
 * The registry series UID of the active structure set's owning dataset.
 * Undefined when no structure set is active, or when its dataset is not
 * registered.
 */
function resolveEditableEntrySeriesUid(
    registry: DatasetEnvironmentRegistry,
    structureSet: StructureSetReadModel | undefined
): SeriesInstanceUid | undefined {
    if (!structureSet) {
        return undefined;
    }
    return registry.entryForStructureSet(structureSet.dicomUid)?.seriesUid;
}
